'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Save, X } from 'lucide-react'
import type { EnemyResult } from '@creanizer/types'

interface EnemyEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (updatedResult: Partial<EnemyResult>) => void
  enemyResult: EnemyResult
  title?: string
}

export function EnemyEditModal({
  isOpen,
  onClose,
  onSave,
  enemyResult,
  title = '編集'
}: EnemyEditModalProps) {
  const [editedName, setEditedName] = useState(enemyResult.basicInfo.name)
  const [editedTitle, setEditedTitle] = useState(enemyResult.basicInfo.title || '')
  const [editedSummary, setEditedSummary] = useState(enemyResult.basicInfo.summary)
  const [editedDescription, setEditedDescription] = useState(enemyResult.basicInfo.description)
  const [editedType, setEditedType] = useState(enemyResult.basicInfo.type)
  const [editedCategory, setEditedCategory] = useState(enemyResult.basicInfo.category)
  const [editedThreat, setEditedThreat] = useState(enemyResult.basicInfo.threat)
  const [editedHabitat, setEditedHabitat] = useState(enemyResult.basicInfo.habitat)
  const [editedAppearance, setEditedAppearance] = useState(enemyResult.appearance.overallDescription)
  const [editedSize, setEditedSize] = useState(enemyResult.appearance.size)
  const [editedBodyType, setEditedBodyType] = useState(enemyResult.appearance.bodyType)
  const [editedVisualImpact, setEditedVisualImpact] = useState(enemyResult.appearance.visualImpact)
  const [editedAIPattern, setEditedAIPattern] = useState(enemyResult.behavior.aiPattern)
  const [editedHuntingPattern, setEditedHuntingPattern] = useState(enemyResult.behavior.huntingPattern)

  // enemyResultが変更されたら編集中の値をリセット
  useEffect(() => {
    setEditedName(enemyResult.basicInfo.name)
    setEditedTitle(enemyResult.basicInfo.title || '')
    setEditedSummary(enemyResult.basicInfo.summary)
    setEditedDescription(enemyResult.basicInfo.description)
    setEditedType(enemyResult.basicInfo.type)
    setEditedCategory(enemyResult.basicInfo.category)
    setEditedThreat(enemyResult.basicInfo.threat)
    setEditedHabitat(enemyResult.basicInfo.habitat)
    setEditedAppearance(enemyResult.appearance.overallDescription)
    setEditedSize(enemyResult.appearance.size)
    setEditedBodyType(enemyResult.appearance.bodyType)
    setEditedVisualImpact(enemyResult.appearance.visualImpact)
    setEditedAIPattern(enemyResult.behavior.aiPattern)
    setEditedHuntingPattern(enemyResult.behavior.huntingPattern)
  }, [enemyResult])

  const handleSave = () => {
    onSave({
      basicInfo: {
        ...enemyResult.basicInfo,
        name: editedName,
        title: editedTitle || undefined,
        summary: editedSummary,
        description: editedDescription,
        type: editedType,
        category: editedCategory,
        threat: editedThreat,
        habitat: editedHabitat
      },
      appearance: {
        ...enemyResult.appearance,
        overallDescription: editedAppearance,
        size: editedSize,
        bodyType: editedBodyType,
        visualImpact: editedVisualImpact
      },
      behavior: {
        ...enemyResult.behavior,
        aiPattern: editedAIPattern,
        huntingPattern: editedHuntingPattern
      }
    })
    onClose()
  }

  const handleCancel = () => {
    // 編集内容をリセット
    setEditedName(enemyResult.basicInfo.name)
    setEditedTitle(enemyResult.basicInfo.title || '')
    setEditedSummary(enemyResult.basicInfo.summary)
    setEditedDescription(enemyResult.basicInfo.description)
    setEditedType(enemyResult.basicInfo.type)
    setEditedCategory(enemyResult.basicInfo.category)
    setEditedThreat(enemyResult.basicInfo.threat)
    setEditedHabitat(enemyResult.basicInfo.habitat)
    setEditedAppearance(enemyResult.appearance.overallDescription)
    setEditedSize(enemyResult.appearance.size)
    setEditedBodyType(enemyResult.appearance.bodyType)
    setEditedVisualImpact(enemyResult.appearance.visualImpact)
    setEditedAIPattern(enemyResult.behavior.aiPattern)
    setEditedHuntingPattern(enemyResult.behavior.huntingPattern)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">名前</Label>
              <Input
                id="name"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder="エネミー名"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="title">称号・二つ名</Label>
              <Input
                id="title"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                placeholder="称号（省略可）"
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="type">タイプ</Label>
              <Input
                id="type"
                value={editedType}
                onChange={(e) => setEditedType(e.target.value)}
                placeholder="エネミータイプ"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="category">カテゴリー</Label>
              <Input
                id="category"
                value={editedCategory}
                onChange={(e) => setEditedCategory(e.target.value)}
                placeholder="カテゴリー"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="threat">脅威度</Label>
              <Select value={editedThreat} onValueChange={setEditedThreat}>
                <SelectTrigger id="threat" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="E">E</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="S">S</SelectItem>
                  <SelectItem value="SS">SS</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="summary">概要</Label>
            <Textarea
              id="summary"
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              placeholder="エネミーの概要"
              rows={2}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="description">詳細説明</Label>
            <Textarea
              id="description"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="詳細な説明"
              rows={4}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="habitat">生息地</Label>
            <Input
              id="habitat"
              value={editedHabitat}
              onChange={(e) => setEditedHabitat(e.target.value)}
              placeholder="生息地"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="appearance">外見の説明</Label>
            <Textarea
              id="appearance"
              value={editedAppearance}
              onChange={(e) => setEditedAppearance(e.target.value)}
              placeholder="外見の全体的な説明"
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="size">サイズ</Label>
              <Input
                id="size"
                value={editedSize}
                onChange={(e) => setEditedSize(e.target.value)}
                placeholder="サイズ"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="bodyType">体型</Label>
              <Input
                id="bodyType"
                value={editedBodyType}
                onChange={(e) => setEditedBodyType(e.target.value)}
                placeholder="体型"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="visualImpact">視覚的インパクト</Label>
            <Textarea
              id="visualImpact"
              value={editedVisualImpact}
              onChange={(e) => setEditedVisualImpact(e.target.value)}
              placeholder="視覚的なインパクト"
              rows={2}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="aiPattern">AIパターン</Label>
              <Textarea
                id="aiPattern"
                value={editedAIPattern}
                onChange={(e) => setEditedAIPattern(e.target.value)}
                placeholder="AIパターン"
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="huntingPattern">狩猟パターン</Label>
              <Textarea
                id="huntingPattern"
                value={editedHuntingPattern}
                onChange={(e) => setEditedHuntingPattern(e.target.value)}
                placeholder="狩猟パターン"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            キャンセル
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}